import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { ORG_SETTINGS } from '@/data/orgSettings';
import type { OrgSettings } from '@/data/orgSettings';

interface UseOrgSettingsResult {
  /**
   * Live mode: `null` until the API responds, then the real server row.
   * Mockup mode: always the ORG_SETTINGS fixture.
   * Callers in live mode must branch on `null` — never default to the
   * fixture, or the wrong org's data flashes on switch.
   */
  data: OrgSettings | null;
  isLoading: boolean;
}

export function useOrgSettings(): UseOrgSettingsResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['orgSettings', orgId],
    queryFn: async () => {
      const token = await getToken();
      const res = await apiFetch<{
        id: string;
        name: string;
        slug: string;
        // Task 152: org.timezone is NOT NULL with default 'UTC'. Every org
        // — including the just-in-time rows ensureSynced creates from a
        // Clerk session — has a real timezone string. The Settings form
        // shows whatever the column holds; first-run orgs see 'UTC'
        // selected until the admin picks a real zone.
        timezone: string;
        minutesPerScreenshot: number;
        fullDayThresholdHours: number;
        halfDayThresholdHours: number;
        halfDayWeight: number;
        // 104e D2 — the backend now echoes this flag on GET so the toggle
        // hydrates correctly. Older responses may omit it; guard with a
        // `??` fallback below so a fresh response shape mismatch does not
        // throw `undefined is not a boolean`.
        autoInviteOnEmployeeDiscovery?: boolean;
        // Task 110 D4 — the backend echoes the per-org email-synthesis
        // domain on GET. Older responses may omit it; guard with `?? null`
        // below so the form input renders empty rather than crashing.
        insightfulEmailDomain?: string | null;
      }>(`/api/v1/orgs/${orgId}`, token);
      return {
        id: res.id,
        name: res.name,
        slug: res.slug,
        timezone: res.timezone,
        minutesPerScreenshot: res.minutesPerScreenshot,
        fullDayThresholdHours: res.fullDayThresholdHours,
        halfDayThresholdHours: res.halfDayThresholdHours,
        halfDayWeight: res.halfDayWeight,
        autoInviteOnEmployeeDiscovery: res.autoInviteOnEmployeeDiscovery ?? false,
        insightfulEmailDomain: res.insightfulEmailDomain ?? null,
        workWeek: { start: 'Monday', end: 'Friday' },
      } satisfies OrgSettings;
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) return { data: ORG_SETTINGS, isLoading: false };
  return { data: query.data ?? null, isLoading: query.isLoading };
}
